import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Dropdown, Groupper, Header, Input, Message, Modal, Table } from 'react-frontier';
import { MetadataItem, MetadataType, PromoterFormMetadata } from '../SiteClasses';
import { bindClick } from '../SiteUtil';
import Toast from '../Toast';
import API from '../API';
import classNames from 'classnames';

interface PromoterFormProps{
	prospect_id: number,
	mode: 'admin' | 'public',
	canEdit?: boolean,
	style?: React.CSSProperties,
}

interface QuestionInputProps{
	prospect_id: number,
	question: PromoterFormMetadata,
	canEdit: boolean,
	onUpdate: (metadata_id: number, value_id: number, value: string)=>void,
}

var QuestionInput = (props: QuestionInputProps)=>{
	var [editing, setEditing] = useState<boolean>(false);
	var [sending, setSending] = useState<boolean>(false);
	var [value, setValue] = useState<string>(null);
	var [options, setOptions] = useState<{ value_id: number, value: string }[]>(null);

	useEffect(()=>{
		setValue(!editing ? null : (props.question.value_id ? props.question.value_id.toString() : (props.question.value || '')));
		setOptions(null);
		if(editing && [MetadataType.DROPDOWN, MetadataType.MULTIPLE].indexOf(props.question.type_id)!==-1){
			API.getMetadataValues(props.question.metadata_id).then(res=>{
				if(res.error){
					setEditing(false);
					return;
				}
				setOptions(res.data);
			});
		}
	}, [editing]);

	var sendUpdate = (value_id: number, val: string)=>{
		if(sending) return;
		if(value_id){
			if(value_id===props.question.value_id) return setEditing(false);
		}else{
			if((val || '').trim()===(props.question.value || '').trim()) return setEditing(false);
		}
		setSending(true);
		API.setPromoterFormAnswer(props.prospect_id, props.question.metadata_id, value_id, val).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Se ha actualizado la respuesta');
			var value_text = val || (value_id ? value_id.toString() : '');
			if(options && value_id){
				var v = options.find(a=>a.value_id===value_id);
				if(v) value_text = v.value;
			}
			props.onUpdate(props.question.metadata_id, value_id, value_text);
			setValue(null);
			setEditing(false);
		}).catch(()=>{
			Toast.error('Hubo un error inesperado actualizando el formulario. (LCL-1)');
		}).finally(()=>{
			setSending(false);
		});
	}

	if(props.question.type_id===MetadataType.CHECKBOX){
		return <div style={{ minHeight: 40, display: 'flex', alignItems: 'center', gap: 8 }}>
			<Checkbox
				checked={value!==null ? value==='1' : props.question.value==='1'}
				label={props.question.metadata_comment || ''}
				onChange={v=>props.canEdit && !sending && sendUpdate(null, v ? '1' : '0')}
			/>
			{sending && <div className="fr loading inline"></div>}
		</div>
	}else if(!editing){
		return <Input
			readonly
			fluid
			textarea={props.question.type_id===MetadataType.TEXTAREA}
			textareaRows={2}
			value={props.question.value}
			placeholder={props.question.metadata_name}
			style={{ margin: 0 }}
			buttons={props.canEdit ? (
				<Button
					iconName='pen'
					onClick={bindClick(setEditing, true)}
					className={classNames({ box: props.question.type_id===MetadataType.TEXTAREA })}
				/>
			) : undefined}
		/>
	}else if(props.question.type_id===MetadataType.DROPDOWN || props.question.type_id===MetadataType.MULTIPLE){
		return <div style={{ display: 'flex', gap: 8 }}>
			<Dropdown
				key={`opts-${options ? options.length : 0}`}
				style={{ flex: 1 }}
				items={!options ? [] : options.map(a=>({ text: a.value, value: a.value_id.toString() }))}
				value={value || null}
				onValueChange={v=>setValue(v || null)}
				placeholder={props.question.metadata_name}
			/>
			<Button
				iconName='check'
				color='green'
				loading={sending}
				onClick={()=>sendUpdate(value ? parseInt(value) : null, null)}
			/>
		</div>
	}else{
		return <Input
			fluid
			textarea={props.question.type_id===MetadataType.TEXTAREA}
			textareaRows={2}
			style={{ margin: 0 }}
			placeholder={props.question.metadata_name}
			value={value ?? ''}
			onChange={setValue}
			buttons={(
				<Button
					iconName='check'
					loading={sending}
					onClick={()=>sendUpdate(null, value)}
					className={classNames({ box: props.question.type_id===MetadataType.TEXTAREA })}
				/>
			)}
		/>
	}
}

interface PublicQuestionInputProps{
	question: PromoterFormMetadata,
	value: string,
	onChange: (metadata_id: number, value: string)=>void,
	options?: { value_id: number, value: string }[],
}

var PublicQuestionInput = (props: PublicQuestionInputProps)=>{
	if(props.question.type_id===MetadataType.CHECKBOX){
		return <Checkbox
			checked={props.value==='1'}
			label={props.question.metadata_comment || props.question.metadata_name}
			onChange={v=>props.onChange(props.question.metadata_id, v ? '1' : '0')}
		/>
	}else if(props.question.type_id===MetadataType.DROPDOWN || props.question.type_id===MetadataType.MULTIPLE){
		return <Dropdown
			label={props.question.metadata_name}
			comment={props.question.metadata_comment}
			items={!props.options ? [] : props.options.map(a=>({ text: a.value, value: a.value_id.toString() }))}
			value={props.value || null}
			onValueChange={v=>props.onChange(props.question.metadata_id, v || '')}
		/>
	}else{
		return <Input
			fluid
			label={props.question.metadata_name}
			comment={props.question.metadata_comment}
			textarea={props.question.type_id===MetadataType.TEXTAREA}
			textareaRows={3}
			value={props.value ?? ''}
			onChange={v=>props.onChange(props.question.metadata_id, v)}
			style={{ marginBottom: 8 }}
		/>
	}
}



interface AddQuestionModalProps{
	open: boolean,
	prospect_id: number,
	currentIds: number[],
	onClose: ()=>void,
	onAdd: (q: PromoterFormMetadata)=>void,
}

var AddQuestionModal = (props: AddQuestionModalProps)=>{
	var [metadata, setMetadata] = useState<MetadataItem[]>(null);
	var [loadError, setLoadError] = useState<string>(null);

	useEffect(()=>{
		if(!props.open) return;
		setMetadata(null);
		setLoadError(null);
		API.getMetadataList().then(res=>{
			if(res.error) return setLoadError(res.message);
			setMetadata(res.data);
		});
	}, [props.open]);

	var available = !metadata ? [] : metadata.filter(a=>props.currentIds.indexOf(a.metadata_id)===-1);

	return <Modal open={props.open} onClose={props.onClose}>
		<Modal.Header text='Agregar pregunta' />
		<Modal.Content>
			{(!metadata || loadError) ? (
				<Header loading={!loadError} text={loadError || 'Cargando preguntas'} />
			) : available.length===0 ? (
				<Header text='No hay preguntas disponibles' subtext='Todas las preguntas ya están en el formulario.' size='small'/>
			) : (
				<Table striped selectable>
					<Table.Head>
						<Table.Row>
							<Table.Cell header collapsing>ID</Table.Cell>
							<Table.Cell header>Nombre</Table.Cell>
						</Table.Row>
					</Table.Head>
					<Table.Body>
						{available.map(a=>(
							<Table.Row key={`MTITM-${a.metadata_id}`} onClick={()=>{
								props.onAdd({
									metadata_id: a.metadata_id,
									metadata_name: a.metadata_name,
									metadata_comment: a.metadata_comment,
									type_id: a.type_id,
									value: '',
									value_id: null,
								});
								props.onClose();
							}}>
								<Table.Cell collapsing value={a.metadata_id} />
								<Table.Cell>
									<div>{a.metadata_name}</div>
									{!!a.metadata_comment && <div style={{ fontSize: 12, color: '#888' }}>{a.metadata_comment}</div>}
								</Table.Cell>
							</Table.Row>
						))}
					</Table.Body>
				</Table>
			)}
		</Modal.Content>
		<Modal.Actions>
			<Button text='Cerrar' basic size='small' onClick={props.onClose} />
		</Modal.Actions>
	</Modal>
}

var PromoterForm = (props: PromoterFormProps)=>{
	var [form, setForm] = useState<PromoterFormMetadata[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [addModalOpen, setAddModalOpen] = useState<boolean>(false);
	var [answers, setAnswers] = useState<{ [metadata_id: number]: string }>({});
	var [dropdownOptions, setDropdownOptions] = useState<{ [metadata_id: number]: { value_id: number, value: string }[] }>({});
	var [submitting, setSubmitting] = useState<boolean>(false);
	var [submitResult, setSubmitResult] = useState<{ type: 'success' | 'error', text: string }>(null);

	useEffect(()=>{
		if(!props.prospect_id) return;
		setForm(null);
		setLoadError(null);
		setSubmitResult(null);

		var loadFn = props.mode==='admin' ? API.getPromoterForm : API.getPublicPromoterForm;
		loadFn(props.prospect_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			var formData: PromoterFormMetadata[] = res.data;
			setForm(formData);

			if(props.mode==='public'){
				var init: { [k: number]: string } = {};
				for(var q of formData) init[q.metadata_id] = q.value || '';
				setAnswers(init);

				var dropdowns = formData.filter(a=>
					a.type_id===MetadataType.DROPDOWN || a.type_id===MetadataType.MULTIPLE
				);
				if(dropdowns.length>0){
					Promise.all(dropdowns.map(q=>
						API.getPublicMetadataValues(q.metadata_id).then(r=>({
							metadata_id: q.metadata_id,
							values: r.error ? [] : r.data,
						}))
					)).then(results=>{
						var opts: typeof dropdownOptions = {};
						for(var r of results) opts[r.metadata_id] = r.values;
						setDropdownOptions(opts);
					});
				}
			}
		}).catch(()=>{
			setLoadError('Hubo un error inesperado cargando el formulario. (LCL-PRMFRM-1)');
		});
	}, [props.prospect_id, props.mode]);

	var onAnswerUpdate = (metadata_id: number, value_id: number, value: string)=>{
		if(!form) return;
		var m = [...form];
		var idx = m.findIndex(a=>a.metadata_id===metadata_id);
		if(idx===-1) return;
		m[idx] = { ...m[idx], value, value_id };
		setForm(m);
	}

	var onPublicAnswerChange = (metadata_id: number, value: string)=>{
		setAnswers(prev=>({ ...prev, [metadata_id]: value }));
	}

	var submitPublicForm = async ()=>{
		if(submitting || !form) return;
		setSubmitting(true);
		setSubmitResult(null);
		try{
			for(var q of form){
				var val = answers[q.metadata_id] ?? '';
				var value_id: number = null;
				if(q.type_id===MetadataType.DROPDOWN || q.type_id===MetadataType.MULTIPLE){
					value_id = val ? parseInt(val) : null;
					val = null;
				}
				var res = await API.setPublicPromoterFormAnswer(props.prospect_id, q.metadata_id, value_id, val);
				if(res.error){
					setSubmitResult({ type: 'error', text: res.message });
					return;
				}
			}
			setSubmitResult({ type: 'success', text: 'Tu información ha sido enviada correctamente.' });
		}catch{
			setSubmitResult({ type: 'error', text: 'Hubo un error inesperado enviando el formulario. (LCL-PRMFRMSUB-1)' });
		}finally{
			setSubmitting(false);
		}
	}

	if(!props.prospect_id) return null;

	if(props.mode==='public'){
		return <Groupper
			title='Formulario de aplicación'
			titleCentered
			style={{ maxWidth: 680, margin: 'auto', ...props.style }}
		>
			{submitResult?.type==='success' ? (
				<div style={{ padding: '40px 20px', textAlign: 'center' }}>
					<Header
						text='¡Formulario enviado!'
						subtext={submitResult.text}
					/>
				</div>
			) : (<>
				{loadError && (
					<Message type='error' header='Error' style={{ margin: 16 }}>{loadError}</Message>
				)}
				{submitResult?.type==='error' && (
					<Message type='error' header='Error al enviar' style={{ margin: 16 }}>{submitResult.text}</Message>
				)}
				{!form && !loadError && (
					<div style={{ padding: 40 }}>
						<Header loading text='Cargando formulario' />
					</div>
				)}
				{form && form.length===0 && (
					<div style={{ padding: 40 }}>
						<Header text='Sin preguntas' subtext='Este formulario no tiene preguntas configuradas.' />
					</div>
				)}
				{form && form.length>0 && (<>
					<Groupper.Divider top />
					<div style={{ padding: '0 16px' }}>
						{form.map(q=>(
							<div key={`PUBQ-${q.metadata_id}`} style={{ marginBottom: 16 }}>
								<PublicQuestionInput
									question={q}
									value={answers[q.metadata_id] ?? ''}
									onChange={onPublicAnswerChange}
									options={dropdownOptions[q.metadata_id]}
								/>
							</div>
						))}
					</div>
					<Groupper.Divider />
					<div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'flex-end' }}>
						<Button
							text='Enviar formulario'
							color='black'
							size='small'
							loading={submitting}
							onClick={submitPublicForm}
						/>
					</div>
				</>)}
			</>)}
		</Groupper>
	}

	var canEdit = props.canEdit!==false;
	return <>
		<Table
			striped
			style={{ maxWidth: 800, margin: 'auto', ...props.style }}
		>
			<Table.Head title="Formulario" titleRight={canEdit ? (
				<Button text="Agregar" iconName="plus" size="small" color="black" onClick={()=>setAddModalOpen(true)} />
			) : undefined}>
				<Table.Row>
					<Table.Cell header>Pregunta</Table.Cell>
					<Table.Cell header>Respuesta</Table.Cell>
				</Table.Row>
			</Table.Head>
			<Table.Body>
				{(!form || loadError) ? (
					<Table.Row>
						<Table.Cell colSpan={2}>
							<Header loading={!loadError} text={loadError || 'Cargando formulario'} style={{ paddingTop: 0 }} />
						</Table.Cell>
					</Table.Row>
				) : form.length===0 ? (
					<Table.Row>
						<Table.Cell colSpan={2} empty>No hay preguntas en el formulario</Table.Cell>
					</Table.Row>
				) : form.map(q=>(
					<Table.Row key={`ADMQ-${q.metadata_id}`}>
						<Table.Cell className='question'>
							<div style={{ fontWeight: 500 }}>{q.metadata_name}</div>
							{!!q.metadata_comment && q.metadata_comment.length>0 && (
								<div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{q.metadata_comment}</div>
							)}
						</Table.Cell>
						<Table.Cell>
							<QuestionInput
								prospect_id={props.prospect_id}
								question={q}
								canEdit={canEdit}
								onUpdate={onAnswerUpdate}
							/>
						</Table.Cell>
					</Table.Row>
				))}
			</Table.Body>
		</Table>
		<AddQuestionModal
			open={addModalOpen}
			prospect_id={props.prospect_id}
			currentIds={form ? form.map(a=>a.metadata_id) : []}
			onClose={bindClick(setAddModalOpen, false)}
			onAdd={q=>setForm(prev=>[...(prev||[]), q])}
		/>
	</>
}

export default PromoterForm;
